import React from 'react';
import styled from 'styled-components';

export const StyledPermissionToggle = styled.div`
  display: inline-flex;
  border: 1px solid ${({ theme }) => theme.palette.border};
  border-radius: ${({ theme }) => theme.radius.small};
  overflow: hidden;
`;

// $mixed highlights an option some (but not all) of a bulk selection currently have — matches the legacy admin's
// yellow "indeterminate" tag color
export const StyledPermissionButton = styled.button<{ $active: boolean; $mixed?: boolean }>`
  padding: 4px 10px;
  font-size: 12px;
  border: none;
  border-right: 1px solid ${({ theme }) => theme.palette.border};
  cursor: pointer;
  background: ${({ $active, $mixed, theme }) => ($mixed ? '#f4dd75' : $active ? theme.palette.accent : 'transparent')};
  color: ${({ theme }) => theme.palette.text};
  font-weight: ${({ $active, $mixed }) => ($active || $mixed ? 600 : 400)};
  transition: background 0.15s;

  &:last-child {
    border-right: none;
  }

  &:hover:not(:disabled) {
    background: ${({ $mixed, theme }) => ($mixed ? '#f4dd75' : theme.palette.accent)};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

export interface PermissionToggleOption {
  value: string;
  label: string;
}

export type PermissionToggleOptionState = 'active' | 'mixed';

interface PermissionToggleProps {
  options: PermissionToggleOption[];
  value: string | undefined;
  onChange: (value: string) => void;
  disabled?: boolean;
  // Per-option override, e.g. for bulk-editing where several users may hold different options at once.
  // Falls back to marking the option matching `value` as active.
  getOptionState?: (optionValue: string) => PermissionToggleOptionState | undefined;
}

export const PermissionToggle: React.FC<PermissionToggleProps> = ({
  options,
  value,
  onChange,
  disabled = false,
  getOptionState,
}) => (
  <StyledPermissionToggle>
    {options.map((opt) => {
      const state = getOptionState ? getOptionState(opt.value) : value === opt.value ? 'active' : undefined;
      return (
        <StyledPermissionButton
          key={opt.value}
          type="button"
          $active={state === 'active'}
          $mixed={state === 'mixed'}
          disabled={disabled}
          onClick={() => onChange(opt.value)}
        >
          {opt.label}
        </StyledPermissionButton>
      );
    })}
  </StyledPermissionToggle>
);
